'use client';

import { Button } from '@/components/ui/button';
import { useUserProfile } from '@/context/UserProfileContext';
import { IonActionSheet, IonAlert } from '@ionic/react';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { toast } from 'sonner';

export function PostMenu({
  username,
  postId,
  onDelete,
  onNavigate,
}: {
  username: string;
  postId: string;
  onDelete: () => void;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { profile } = useUserProfile();
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const isOwner = profile?.username === username;

  const handleCopyLink = () => {
    const url = `${window.location.origin}/p/${postId}`;
    navigator.clipboard.writeText(url);
    toast.success(t('link_copied'));
    setIsOpen(false);
  };

  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(true)}>
        <MoreHorizontal className="size-5" />
      </Button>

      <IonActionSheet
        isOpen={isOpen}
        onDidDismiss={() => setIsOpen(false)}
        buttons={[
          {
            text: t('see_profile'),
            handler: () => {
              history.push(`/u/${username}`)
              onNavigate();
              setIsOpen(false);
            }
          },
          {
            text: t('copy_link'),
            handler: handleCopyLink,
          },
          {
            text: t('report'),
            role: 'destructive',
          },
          ...(isOwner
            ? [
              {
                text: t('delete'),
                role: 'destructive',
                handler: () => {
                  setIsOpen(false);
                  setShowDeleteAlert(true);
                }
              }
            ] : []),
          {
            text: t('cancel'),
            role: 'cancel',
          }
        ]}
      />

      {/* Delete Confirmation Alert */}
      <IonAlert
        isOpen={showDeleteAlert}
        header={t('confirm_delete', 'Confirmation')}
        message={t('confirm_delete_post_message', 'Êtes-vous sûr de vouloir supprimer cette publication ?')}
        buttons={[
          {
            text: t('cancel', 'Annuler'),
            role: 'cancel',
            handler: () => {
              setShowDeleteAlert(false);
            },
          },
          {
            text: t('delete', 'Supprimer'),
            role: 'confirm',
            cssClass: 'alert-button-delete',
            handler: () => {
              onDelete();
              setShowDeleteAlert(false);
              toast.success(t('post_deleted', 'Publication supprimée'));
            },
          },
        ]}
        onDidDismiss={() => setShowDeleteAlert(false)}
      />
    </>
  );
}
